import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DEFAULT_NUMBER_FORMAT, formatForColumn, isNumericColumn, newId } from '@tallyframe/core';
/**
 * Add-a-widget buttons.
 *
 * Every default is bound to a column that actually exists in the passed
 * dataset, and to an aggregation that column's type supports, so the operation
 * this emits always survives core's `validateDefinition`. When a dataset has no
 * numeric column at all the fallback is `{kind:'count'}`, which works on
 * anything.
 *
 * Each operation carries a client-minted widget id. The host applies these
 * operations locally for instant feedback and then sends the same objects to the
 * server; if the id were minted inside the operation, the two applications would
 * invent different ids and the new widget would change identity under the
 * user, losing the selection and desynchronising the undo stack.
 */
function numericColumns(dataset) {
    return dataset.columns.filter(isNumericColumn);
}
/** Best category axis available: a plain string column, else a date, else
 *  whatever the first column happens to be. Any type groups correctly. */
function dimensionFor(dataset, prefer) {
    const preferred = dataset.columns.find((c) => c.type === prefer);
    const other = dataset.columns.find((c) => c.type === (prefer === 'string' ? 'date' : 'string'));
    return preferred ?? other ?? dataset.columns[0];
}
function kpiOperation(dataset) {
    const numeric = numericColumns(dataset)[0];
    const metric = numeric ? { kind: 'sum', columnId: numeric.id } : { kind: 'count' };
    return {
        op: 'add_widget',
        widget: {
            id: newId('w'),
            type: 'kpi',
            title: numeric ? `Total ${numeric.label}` : 'Row count',
            metric,
            format: formatForColumn(numeric)
        }
    };
}
function chartOperation(dataset, chartType) {
    const dimension = dimensionFor(dataset, chartType === 'line' ? 'date' : 'string');
    const numeric = numericColumns(dataset)[0];
    const series = numeric
        ? [{ id: numeric.id, label: numeric.label, metric: { kind: 'sum', columnId: numeric.id } }]
        : [{ id: 'row_count', label: 'Row count', metric: { kind: 'count' } }];
    return {
        op: 'add_widget',
        widget: {
            id: newId('w'),
            type: 'chart',
            title: numeric && dimension ? `${numeric.label} by ${dimension.label}` : 'New chart',
            chartType,
            dimensionColumnId: dimension?.id ?? '',
            series,
            format: { ...DEFAULT_NUMBER_FORMAT },
            maxCategories: 24,
            stacked: false
        }
    };
}
function tableOperation(dataset) {
    return {
        op: 'add_widget',
        widget: {
            id: newId('w'),
            type: 'table',
            title: 'Detail',
            columns: dataset.columns.slice(0, 6).map((column) => ({ columnId: column.id })),
            pageSize: 25,
            defaultExpandedDepth: 1
        }
    };
}
function textOperation() {
    return {
        op: 'add_widget',
        widget: { id: newId('w'), type: 'text', title: 'Note', body: '', authored: true }
    };
}
export function WidgetPalette(props) {
    const { dataset, disabled = false } = props;
    const usable = dataset.columns.length > 0;
    const add = (operation) => () => props.onOperations([operation]);
    return (_jsxs("aside", { className: "tf-panel tf-palette", "aria-label": "Add to report", children: [_jsx("h2", { className: "tf-panel-title", children: "Add to report" }), usable ? (_jsxs("div", { className: "tf-palette-list", children: [_jsx("button", { type: "button", className: "tf-btn tf-btn--block", disabled: disabled, onClick: add(kpiOperation(dataset)), children: "KPI" }), _jsx("button", { type: "button", className: "tf-btn tf-btn--block", disabled: disabled, onClick: add(chartOperation(dataset, 'bar')), children: "Bar chart" }), _jsx("button", { type: "button", className: "tf-btn tf-btn--block", disabled: disabled, onClick: add(chartOperation(dataset, 'line')), children: "Line chart" }), _jsx("button", { type: "button", className: "tf-btn tf-btn--block", disabled: disabled, onClick: add(tableOperation(dataset)), children: "Table" }), _jsx("button", { type: "button", className: "tf-btn tf-btn--block", disabled: disabled, onClick: add(textOperation()), children: "Note" })] })) : (_jsx("p", { className: "tf-hint", children: "This dataset has no columns, so there is nothing to chart yet." })), _jsx("p", { className: "tf-hint", children: "New widgets are added below the existing ones with a default binding you can change in the inspector." })] }));
}
//# sourceMappingURL=WidgetPalette.js.map